import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Accordion as RAccordion, Tabs as RTabs } from 'radix-ui';
import { motion, useReducedMotion } from 'motion/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';
import { springs } from '../lib/motion';

// ---------------------------------------------------------------- TopBar
/**
 * Barra superior: ação à esquerda (menu/voltar), título, ações à direita.
 * `dark` (padrão do app no celular) ou `light` (cabeçalho de página no desktop).
 */
export function TopBar({
  left,
  title,
  subtitle,
  right,
  variant = 'dark',
  className,
  children,
}: {
  left?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  variant?: 'dark' | 'light';
  className?: string;
  /** Conteúdo extra abaixo da linha principal (ex.: abas). */
  children?: ReactNode;
}) {
  return (
    <header
      className={cn(
        'safe-top sticky top-0 z-30',
        variant === 'dark'
          ? 'border-b border-dark-line bg-dark text-on-dark'
          : 'border-b border-line bg-surface/85 text-ink backdrop-blur-xl backdrop-saturate-150',
        className,
      )}
    >
      <div className="flex h-14 items-center gap-1 px-2 sm:px-3">
        {left ? <div className="flex shrink-0 items-center">{left}</div> : <div className="w-2" />}
        <div className="min-w-0 flex-1 px-1">
          {typeof title === 'string' ? <h1 className="truncate text-[17px] font-semibold tracking-[-0.015em]">{title}</h1> : title}
          {subtitle ? <p className={cn('truncate text-xs', variant === 'dark' ? 'text-white/65' : 'text-ink-3')}>{subtitle}</p> : null}
        </div>
        {right ? <div className="flex shrink-0 items-center gap-0.5">{right}</div> : null}
      </div>
      {children}
    </header>
  );
}

// ---------------------------------------------------------------- FloatingBar
/**
 * Barra fixa no rodapé para resumo de valores ou confirmação de ações.
 * Com a barra de abas do celular visível, o app define --bottom-nav-offset (altura da barra de abas) e
 * --bottom-nav-safe (folga embaixo no lugar da área segura, que a barra de abas já cobre): a barra sobe e
 * fica logo acima. Sem as variáveis, nada muda.
 */
export function FloatingBar({
  children,
  variant = 'light',
  className,
  inline,
}: {
  children: ReactNode;
  variant?: 'light' | 'dark';
  className?: string;
  inline?: boolean;
}) {
  return (
    <div
      className={cn(
        inline
          ? 'safe-bottom sticky bottom-0'
          : 'fixed inset-x-0 bottom-[var(--bottom-nav-offset,0px)] pb-[var(--bottom-nav-safe,max(env(safe-area-inset-bottom),0px))] lg:left-[var(--sidebar-width,0px)]',
        'z-20 animate-slide-up',
        variant === 'dark'
          ? 'border-t border-dark-line bg-dark text-on-dark'
          : 'border-t border-line bg-surface/90 shadow-float backdrop-blur-xl backdrop-saturate-150',
        className,
      )}
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">{children}</div>
    </div>
  );
}

/** Espaço reservado no fim da página para o conteúdo não ficar atrás da FloatingBar. */
export function FloatingBarSpacer({ size = 'md' }: { size?: 'md' | 'lg' }) {
  return <div aria-hidden="true" className={size === 'lg' ? 'h-32' : 'h-24'} />;
}

// ---------------------------------------------------------------- Tabs (sublinhado que desliza)
export function Tabs({
  value,
  onValueChange,
  tabs,
  className,
  variant = 'light',
  children,
}: {
  value: string;
  onValueChange: (v: string) => void;
  tabs: { value: string; label: ReactNode; badge?: ReactNode }[];
  className?: string;
  variant?: 'light' | 'dark';
  children?: ReactNode;
}) {
  const layoutId = useId();
  const reduce = useReducedMotion();
  const dark = variant === 'dark';
  return (
    <RTabs.Root value={value} onValueChange={onValueChange} className={className}>
      <RTabs.List className={cn('flex', dark ? 'bg-dark' : 'border-b border-line bg-surface')} aria-label="Seções">
        {tabs.map((t) => {
          const active = t.value === value;
          return (
            <RTabs.Trigger
              key={t.value}
              value={t.value}
              className={cn(
                'relative flex h-12 flex-1 items-center justify-center gap-2 px-4 text-sm font-semibold tracking-[-0.005em] transition-colors duration-150',
                dark
                  ? 'text-white/60 hover:text-white data-[state=active]:text-white'
                  : 'text-ink-3 hover:text-ink-2 data-[state=active]:text-ink',
              )}
            >
              {t.label}
              {t.badge}
              {active ? (
                <motion.span
                  layoutId={`tab-${layoutId}`}
                  transition={reduce ? { duration: 0 } : springs.snappy}
                  aria-hidden="true"
                  className={cn('absolute inset-x-3 bottom-0 h-[2.5px] rounded-t-full', dark ? 'bg-primary-bright' : 'bg-primary')}
                />
              ) : null}
            </RTabs.Trigger>
          );
        })}
      </RTabs.List>
      {children}
    </RTabs.Root>
  );
}

export const TabPanel = RTabs.Content;

// ---------------------------------------------------------------- Pills de categoria (scroll horizontal)
export function PillTabs<T extends string>({
  value,
  onChange,
  items,
  className,
  variant = 'light',
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: ReactNode; count?: number }[];
  className?: string;
  variant?: 'light' | 'dark';
}) {
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const layoutId = useId();
  const reduce = useReducedMotion();
  const dark = variant === 'dark';
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }, [value, reduce]);
  return (
    <div className={cn('scrollbar-none flex snap-x gap-2 overflow-x-auto px-4 py-2.5', className)} role="tablist">
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            ref={active ? activeRef : undefined}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.value)}
            className={cn(
              'relative isolate h-9 shrink-0 snap-start rounded-full px-4 text-[13.5px] font-semibold tracking-[-0.005em] transition-[color,background-color,scale] duration-150 active:scale-[0.97] motion-reduce:active:scale-100',
              active
                ? dark
                  ? 'text-on-accent'
                  : 'text-bg'
                : dark
                  ? 'bg-white/10 text-white/80 hover:bg-white/15 hover:text-white'
                  : 'bg-surface text-ink-2 shadow-control ring-1 ring-inset ring-line hover:text-ink',
            )}
          >
            {active ? (
              <motion.span
                layoutId={`pill-${layoutId}`}
                transition={reduce ? { duration: 0 } : springs.snappy}
                aria-hidden="true"
                className={cn('absolute inset-0 -z-10 rounded-full', dark ? 'bg-primary-bright' : 'bg-ink')}
              />
            ) : null}
            {it.label}
            {it.count !== undefined ? <span className="tabular ml-1.5 opacity-60">{it.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Listas
export function List({ children, className, inset }: { children: ReactNode; className?: string; inset?: boolean }) {
  return (
    <div className={cn('divide-y divide-line overflow-hidden bg-surface', inset ? 'rounded-card border border-line shadow-card' : '', className)}>
      {children}
    </div>
  );
}

/**
 * Linha de lista (clientes, produtos, itens de menu de configurações).
 * Com onClick/href vira botão com chevron.
 */
export function ListItem({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  chevron,
  className,
  danger,
  as: As,
  badge,
  disabled,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
  danger?: boolean;
  as?: 'div' | 'button';
  badge?: ReactNode;
  disabled?: boolean;
}) {
  const interactive = Boolean(onClick);
  const Tag = (As ?? (interactive ? 'button' : 'div')) as 'button';
  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group/item flex w-full items-center gap-3 px-4 py-3 text-left',
        interactive && 'transition-colors duration-100 hover:bg-ink/[0.03] active:bg-ink/[0.06] disabled:opacity-50',
        className,
      )}
    >
      {leading ? <div className={cn('shrink-0 [&_svg]:size-5', danger ? 'text-danger' : 'text-ink-3')}>{leading}</div> : null}
      <div className="min-w-0 flex-1">
        <div className={cn('flex items-center gap-2 text-[15px] font-medium', danger ? 'text-danger' : 'text-ink')}>
          <span className="truncate">{title}</span>
          {badge}
        </div>
        {subtitle ? <div className="mt-0.5 truncate text-sm text-ink-2">{subtitle}</div> : null}
      </div>
      {trailing ? <div className="shrink-0 text-right text-sm text-ink-2">{trailing}</div> : null}
      {(chevron ?? interactive) ? (
        <ChevronRight
          className="size-5 shrink-0 text-ink-3 transition-transform duration-150 group-hover/item:translate-x-0.5 motion-reduce:transition-none"
          aria-hidden="true"
        />
      ) : null}
    </Tag>
  );
}

// ---------------------------------------------------------------- Acordeão
export function Accordion({
  title,
  subtitle,
  children,
  defaultOpen,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  return (
    <RAccordion.Root type="single" collapsible defaultValue={defaultOpen ? 'item' : undefined} className={className}>
      <RAccordion.Item value="item" className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        <RAccordion.Header>
          <RAccordion.Trigger className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-ink/[0.02]">
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold text-ink">{title}</div>
              {subtitle ? <div className="mt-0.5 text-sm text-ink-2">{subtitle}</div> : null}
            </div>
            <ChevronDown
              className="size-5 shrink-0 text-ink-3 transition-transform duration-200 ease-snappy group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          </RAccordion.Trigger>
        </RAccordion.Header>
        <RAccordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="border-t border-line px-4 py-4">{children}</div>
        </RAccordion.Content>
      </RAccordion.Item>
    </RAccordion.Root>
  );
}
